import { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import { loadGoogleMaps, getGoogleMapsKey, NAPLES_CENTER } from "@/lib/googleMaps";

// Loads the Google Maps JS API once (if key is present) and renders a dark map.
// Props:
//   markers = [{ id, lat, lng, color, label, title }]
//   center  = { lat, lng }
//   height  = css height
//   onMapClick = ({ lat, lng }) => void
// Imperative: ref.current.focus({lat,lng, zoom})
const MAP_PREF_KEY = "mtn.dispatch.map.pref.v1";
const MapView = forwardRef(function MapView({ markers = [], center, height = "100%", onMapClick }, ref) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerObjsRef = useRef([]);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  const apiKey = getGoogleMapsKey();

  useImperativeHandle(ref, () => ({
    focus: ({ lat, lng, zoom = 15 }) => {
      if (!mapRef.current || lat == null || lng == null) return;
      mapRef.current.panTo({ lat, lng });
      mapRef.current.setZoom(zoom);
    },
    // Let the dispatcher pin the current pan/zoom as their new default.
    saveAsDefault: () => {
      if (!mapRef.current) return null;
      const c = mapRef.current.getCenter();
      const pref = { lat: c.lat(), lng: c.lng(), zoom: mapRef.current.getZoom() };
      try { localStorage.setItem(MAP_PREF_KEY, JSON.stringify(pref)); } catch (_) { /* localStorage unavailable */ }
      return pref;
    },
    // Wipe stored preference and re-center on Golden Gate City.
    resetDefault: () => {
      try { localStorage.removeItem(MAP_PREF_KEY); } catch (_) { /* localStorage unavailable */ }
      if (!mapRef.current) return;
      mapRef.current.panTo(NAPLES_CENTER);
      mapRef.current.setZoom(14);
    },
  }), []);

  useEffect(() => {
    if (!apiKey) return;
    let mounted = true;
    loadGoogleMaps()
      .then(() => { if (mounted) setReady(true); })
      .catch((err) => {
        console.error("MapView load failed:", err);
        if (mounted) setError("No se pudo cargar Google Maps.");
      });
    return () => { mounted = false; };
  }, [apiKey]);

  useEffect(() => {
    if (!ready || !containerRef.current || mapRef.current) return;
    // Prefer the dispatcher's saved center/zoom, then the `center` prop, then
    // Golden Gate City (34116) as the default. Zoom 14 keeps the neighborhood
    // in view without dropping the driver dots.
    let saved = null;
    try {
      const raw = localStorage.getItem(MAP_PREF_KEY);
      if (raw) saved = JSON.parse(raw);
    } catch (_) { /* ignore corrupted pref */ }
    const initialCenter = saved && saved.lat != null && saved.lng != null
      ? { lat: saved.lat, lng: saved.lng }
      : (center || NAPLES_CENTER);
    const initialZoom = saved?.zoom || 14;
    mapRef.current = new window.google.maps.Map(containerRef.current, {
      center: initialCenter,
      zoom: initialZoom,
      disableDefaultUI: true,
      zoomControl: true,
      styles: darkMapStyle,
    });
    // Persist the dispatcher's pan/zoom automatically so the next time they
    // open the console the map opens exactly where they left it. `idle` fires
    // after any pan/zoom settles, which is far cheaper than listening to
    // continuous events.
    mapRef.current.addListener("idle", () => {
      try {
        const c = mapRef.current.getCenter();
        if (!c) return;
        localStorage.setItem(
          MAP_PREF_KEY,
          JSON.stringify({ lat: c.lat(), lng: c.lng(), zoom: mapRef.current.getZoom() })
        );
      } catch (_) { /* ignore */ }
    });
    if (onMapClick) {
      mapRef.current.addListener("click", (e) =>
        onMapClick({ lat: e.latLng.lat(), lng: e.latLng.lng() })
      );
    }
  }, [ready, center, onMapClick]);

  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;
    markerObjsRef.current.forEach((m) => m.setMap(null));
    markerObjsRef.current = markers
      .filter((m) => m.lat != null && m.lng != null)
      .map((m) => {
        // Driver marker — colored circle badge showing driver initials so
        // dispatch can tell who is who at a glance. `m.label` is the 1-2
        // letter code passed in from the caller.
        if (m.iconType === "car") {
          const color = m.color || "#6B7280";
          const initials = (m.label || "?").slice(0, 2).toUpperCase();
          const fontSize = initials.length >= 2 ? 16 : 20;
          const svg = encodeURIComponent(
            `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">`
            + `<circle cx="20" cy="20" r="17" fill="${color}" stroke="#0B0F19" stroke-width="2.5"/>`
            + `<text x="20" y="20" text-anchor="middle" dominant-baseline="central" `
            + `font-family="ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,sans-serif" `
            + `font-size="${fontSize}" font-weight="800" fill="#0B0F19">${initials}</text>`
            + `</svg>`
          );
          return new window.google.maps.Marker({
            position: { lat: m.lat, lng: m.lng },
            map: mapRef.current,
            title: m.title,
            icon: {
              url: `data:image/svg+xml;charset=UTF-8,${svg}`,
              scaledSize: new window.google.maps.Size(40, 40),
              anchor: new window.google.maps.Point(20, 20),
            },
          });
        }
        // Shopping-cart pin for delivery destinations. Drawn as a droplet-shaped
        // marker so the anchor point is at the very bottom (on the exact address).
        // Rendered at 50% size (20×26) so it doesn't dominate the map next to
        // driver dots and regular pickup pins.
        if (m.iconType === "cart") {
          const color = m.color || "#F59E0B";
          const svg = encodeURIComponent(
            `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">`
            + `<path d="M20 51 C 6 34, 2 26, 2 18 A 18 18 0 1 1 38 18 C 38 26, 34 34, 20 51 Z" fill="${color}" stroke="#0B0F19" stroke-width="2.5"/>`
            + `<g transform="translate(10,8)" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">`
            + `<circle cx="8" cy="21" r="1.5"/>`
            + `<circle cx="19" cy="21" r="1.5"/>`
            + `<path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/>`
            + `</g></svg>`
          );
          return new window.google.maps.Marker({
            position: { lat: m.lat, lng: m.lng },
            map: mapRef.current,
            title: m.title,
            icon: {
              url: `data:image/svg+xml;charset=UTF-8,${svg}`,
              scaledSize: new window.google.maps.Size(20, 26),
              anchor: new window.google.maps.Point(10, 25),
            },
          });
        }
        return new window.google.maps.Marker({
          position: { lat: m.lat, lng: m.lng },
          map: mapRef.current,
          title: m.title || m.label,
          label: m.label ? { text: m.label, color: "#000", fontWeight: "700", fontSize: "11px" } : undefined,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: m.color || "#FBBF24",
            fillOpacity: 1,
            strokeColor: "#0B0F19",
            strokeWeight: 2,
          },
        });
      });
  }, [markers, ready]);

  if (!apiKey) {
    return (
      <div
        className="w-full h-full grid place-items-center bg-[#0B0F19] border border-border rounded-md relative overflow-hidden"
        style={{ height }}
        data-testid="map-placeholder"
      >
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 30% 30%, rgba(6,182,212,0.18), transparent 40%), radial-gradient(circle at 70% 70%, rgba(251,191,36,0.15), transparent 45%), repeating-linear-gradient(0deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 40px), repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 40px)",
          }}
        />
        <div className="relative text-center max-w-sm p-6">
          <div className="text-xs font-mono text-primary tracking-widest mb-2">MAP · OFFLINE</div>
          <div className="text-lg font-bold text-white mb-2">Google Maps no configurado</div>
          <div className="text-sm text-muted-foreground">
            Agrega <code className="font-mono text-primary">REACT_APP_GOOGLE_MAPS_KEY</code> en <code className="font-mono">/app/frontend/.env</code> y reinicia el frontend para ver el mapa en vivo de Naples.
          </div>
          {markers.length > 0 && (
            <div className="mt-6 text-left space-y-1 text-xs font-mono max-h-40 overflow-auto">
              {markers.map((m) => (
                <div key={m.id} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: m.color || "#FBBF24" }} />
                  <span className="text-white">{m.title || m.label}</span>
                  <span className="text-muted-foreground">
                    {m.lat != null ? `${m.lat.toFixed(4)}, ${m.lng.toFixed(4)}` : "sin ubicación"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }
  if (error) return <div className="p-4 text-destructive">{error}</div>;
  return <div ref={containerRef} style={{ width: "100%", height }} data-testid="map-view" />;
});

export default MapView;

const darkMapStyle = [
  { elementType: "geometry", stylers: [{ color: "#F5F5F0" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#4B5563" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#FFFFFF" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#FFFFFF" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#E5E7EB" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#FDE68A" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#F59E0B" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#BAE6FD" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#0369A1" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#ECFCCB" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#D9F99D" }] },
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#9CA3AF" }] },
];
